import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule }
	from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../../../shared/animations/page-transitions';
import { PersonMember } from '../../../../../shared/models/user-info';
import { UserService } from '../../../../../shared/services/user.service';
import { OrganizationUserInfo } from '../../../../models/organization-user-info';
import { UserCardService } from '../../../../services/user-card.service';
import { ADMIN_USER_ROLE } from '../../../../models/organization';

interface DetailsModel {
	title: string;
	userRole?: PersonMember;
	usersList?: OrganizationUserInfo[];
	userId?: number;
	orgId: number;
}

@Component({
	selector: 'copo-dialog-delete',
	templateUrl: './dialog-delete.component.html',
	styleUrl: './dialog-delete.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatDialogModule,
		TranslateModule,
		NgIf,

		MatFormFieldModule,

		MatSelectModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,

		MatOptionModule,

		MatButtonModule,
	],
})
export class DialogDeleteComponent implements OnInit, OnDestroy {
	organizationNameAndId?: string;
	userList?: OrganizationUserInfo[];
	dummyUserList?: OrganizationUserInfo[];
	userRole?: string;
	showAdminDeletionDialog?: boolean;
	selectedNewAdmin: FormControl;
	updateUserSubscription: Subscription = new Subscription();
	isSubmitting = false;

	constructor(

		@Inject(MAT_DIALOG_DATA) public data: DetailsModel,

		private readonly dialogRef: MatDialogRef<DialogDeleteComponent>,
		private readonly userService: UserService,
		private readonly userCardService: UserCardService,
	) {
		this.userList = this.data.usersList;
		this.userRole = this.normalizeValue(this.data.userRole?.role);
		const activeAdmins = (this.userList ?? [])
			.filter(user => this.isActiveUser(user) && this.isAdminRole(user.personMembers.role));
		this.selectedNewAdmin = new FormControl('', [Validators.required]);
		if (activeAdmins.length === 1 && this.userRole === ADMIN_USER_ROLE) {
			const deletingAdmin = activeAdmins.find(admin => admin.id === this.data.userId);
			if (deletingAdmin) {
				this.showAdminDeletionDialog = true;
				this.dummyUserList = (this.userList ?? [])
					.filter(user => user.id !== this.data.userId && this.isEligiblePromoteCandidate(user));
			}
		}
		this.organizationNameAndId = `${ this.userService.user.currentVisibility.organizationName }(`
			+ `${ this.userService.user.currentVisibility.organizationId })`;
	}

	ngOnInit() {
		this.dialogRef.updateSize('40%');
	}

	onValidateClick() {
		if (this.isSubmitting) {
			return;
		}
		if (this.showAdminDeletionDialog) {
			if (this.selectedNewAdmin.value) {
				this.updateUserRole(this.data.orgId);
			} else {
				this.dialogRef.disableClose = true;
			}
		} else {
			this.isSubmitting = true;
			this.dialogRef.close(true);
		}
	}

	updateUserRole(orgId: number) {
		if (this.isSubmitting) {
			return;
		}
		this.isSubmitting = true;
		this.updateUserSubscription = this.userCardService.updateUserRole(
			orgId,
			<number> this.selectedNewAdmin.value,
			ADMIN_USER_ROLE
		)
			.subscribe({
				next: () => {
					this.isSubmitting = false;
					this.dialogRef.close(true);
				},
				error: (err: unknown) => {
					this.isSubmitting = false;
					console.error('error updating user role', err);
				},
			});
	}

	ngOnDestroy() {
		this.updateUserSubscription.unsubscribe();
	}

	private isActiveUser(user: OrganizationUserInfo): boolean {
		return this.normalizeValue(user.personMembers.status) === 'activ'
			&& this.normalizeValue(user.status) === 'activ';
	}

	private isAdminRole(role?: string): boolean {
		return this.normalizeValue(role) === 'dsu';
	}

	private isEligiblePromoteCandidate(user: OrganizationUserInfo): boolean {
		if (!this.isActiveUser(user)) {
			return false;
		}

		const normalizedRole = this.normalizeValue(user.personMembers.role);
		return normalizedRole === 'user' || normalizedRole === 'hlpdk';
	}

	private normalizeValue(value?: string): string {
		return (value ?? '').trim().toLowerCase();
	}
}
